import { z } from 'zod';
import { StorageDayType, StorageStartRule, TaxMode, amount } from './schemas/common.js';

/**
 * Conditions de gardiennage. Copiées dans l'intervention à l'entrée (figées) : changer un réglage
 * du garage n'affecte jamais un dossier ouvert.
 */
export const StorageTerms = z.object({
  freeDays: z.int().min(0).max(365),
  dayType: StorageDayType,
  startRule: StorageStartRule,
  dailyFee: amount(),
  feeTaxMode: TaxMode,
  termsText: z.string(),
});
export type StorageTerms = z.infer<typeof StorageTerms>;

/** Rappel J-2 : seulement si le délai gratuit dépasse ce nombre de jours (docs/DECISIONS.md). */
export const REMINDER_DAYS_BEFORE = 2;

export function reminderApplies(freeDays: number): boolean {
  return freeDays > REMINDER_DAYS_BEFORE;
}

// ─── Dates locales dans le fuseau du garage, sans dépendance ────────────────

interface LocalDate {
  y: number;
  m: number; // 1-12
  d: number;
}

function localDateOf(instant: Date, timeZone: string): LocalDate {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(instant);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value);
  return { y: get('year'), m: get('month'), d: get('day') };
}

/** Décalage (ms) du fuseau à un instant donné : heure locale − UTC. */
function offsetMs(instant: Date, timeZone: string): number {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  }).formatToParts(instant);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value);
  const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
  return asUtc - Math.floor(instant.getTime() / 1000) * 1000;
}

/** Instant UTC correspondant à une heure locale du fuseau (gère les changements d'heure). */
export function zonedTimeToUtc(
  local: LocalDate & { h: number; min: number; s: number; ms: number },
  timeZone: string,
): Date {
  const naive = Date.UTC(local.y, local.m - 1, local.d, local.h, local.min, local.s, local.ms);
  let guess = naive - offsetMs(new Date(naive), timeZone);
  guess = naive - offsetMs(new Date(guess), timeZone);
  return new Date(guess);
}

function addDays(date: LocalDate, days: number): LocalDate {
  const t = new Date(Date.UTC(date.y, date.m - 1, date.d + days));
  return { y: t.getUTCFullYear(), m: t.getUTCMonth() + 1, d: t.getUTCDate() };
}

function weekday(date: LocalDate): number {
  return new Date(Date.UTC(date.y, date.m - 1, date.d)).getUTCDay(); // 0 = dimanche
}

/** Dimanche de Pâques (algorithme de Meeus/Jones/Butcher). */
function easterSunday(year: number): LocalDate {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return { y: year, m: month, d: day };
}

const key = (d: LocalDate) => `${d.y}-${String(d.m).padStart(2, '0')}-${String(d.d).padStart(2, '0')}`;

/** Jours fériés nationaux (France métropolitaine, hors Alsace-Moselle). */
export function frenchPublicHolidays(year: number): Set<string> {
  const easter = easterSunday(year);
  const fixed: LocalDate[] = [
    { y: year, m: 1, d: 1 },
    { y: year, m: 5, d: 1 },
    { y: year, m: 5, d: 8 },
    { y: year, m: 7, d: 14 },
    { y: year, m: 8, d: 15 },
    { y: year, m: 11, d: 1 },
    { y: year, m: 11, d: 11 },
    { y: year, m: 12, d: 25 },
  ];
  const movable = [addDays(easter, 1), addDays(easter, 39), addDays(easter, 50)];
  return new Set([...fixed, ...movable].map(key));
}

function isBusinessDay(date: LocalDate): boolean {
  const wd = weekday(date);
  if (wd === 0 || wd === 6) return false;
  return !frenchPublicHolidays(date.y).has(key(date));
}

/**
 * Échéance du retrait gratuit : fin (23:59:59.999, heure locale du garage) du N-ième jour suivant
 * le jour de mise à disposition. En jours ouvrés, samedis, dimanches et jours fériés ne comptent pas.
 * Avec N = 0, l'échéance est la fin du jour de mise à disposition.
 */
export function computeFreeUntil(
  readyAt: Date,
  terms: Pick<StorageTerms, 'freeDays' | 'dayType'>,
  timeZone: string,
): Date {
  let day = localDateOf(readyAt, timeZone);
  let remaining = terms.freeDays;
  while (remaining > 0) {
    day = addDays(day, 1);
    if (terms.dayType === 'calendar' || isBusinessDay(day)) remaining -= 1;
  }
  return zonedTimeToUtc({ ...day, h: 23, min: 59, s: 59, ms: 999 }, timeZone);
}
